import { useEffect, useState } from "react"
import { IconUserPlus } from "@tabler/icons-react"
import AdminAlert from "./AdminAlert"
import apiClient from "../../lib/api"

type Props = {
  open: boolean
  onClose: () => void
}

const inputClass =
  "w-full rounded-xl border border-[#e2e8f0] bg-[#fafafa] px-3.5 py-3 font-dm-sans text-base outline-none transition focus:border-[#00205c]/40 focus:ring-2 focus:ring-[#00205c]/10"

const AdminSignupModal = ({ open, onClose }: Props) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [open, onClose])

  if (!open) return null

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setSuccess(null)

    const trimmedEmail = email.trim().toLowerCase()
    if (!trimmedEmail || !password) {
      setError("Enter an email and a passkey.")
      return
    }
    if (password.length < 8) {
      setError("Passkey must be at least 8 characters.")
      return
    }
    if (password !== confirmPassword) {
      setError("Passkeys do not match.")
      return
    }

    setSubmitting(true)
    try {
      await apiClient.registerAdmin(trimmedEmail, password)
      setSuccess(`${trimmedEmail} can now sign in.`)
      setEmail("")
      setPassword("")
      setConfirmPassword("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create that account.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-md">
        <div className="mb-5 flex items-start gap-3">
          <span className="rounded-xl bg-[#00205c]/10 p-2 text-[#00205c]">
            <IconUserPlus size={20} stroke={1.75} />
          </span>
          <div>
            <h3 className="font-outfit text-xl font-semibold text-[#1f1f1f]">Create admin access</h3>
            <p className="mt-1 font-dm-sans text-sm text-[#787878]">
              Add an email that can sign in to the admin CMS.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex flex-col gap-1.5">
            <span className="font-dm-sans text-sm font-medium text-[#2e2e2e]">Email</span>
            <input
              type="email"
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              className={inputClass}
              required
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="font-dm-sans text-sm font-medium text-[#2e2e2e]">Passkey</span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="At least 8 characters"
              className={inputClass}
              required
              minLength={8}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="font-dm-sans text-sm font-medium text-[#2e2e2e]">Confirm passkey</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Repeat passkey"
              className={inputClass}
              required
              minLength={8}
            />
          </label>

          {error && <AdminAlert variant="error" message={error} onDismiss={() => setError(null)} />}
          {success && <AdminAlert variant="success" message={success} onDismiss={() => setSuccess(null)} />}

          <div className="modal-action mt-2">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Close
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Creating account…" : "Create account"}
            </button>
          </div>
        </form>
      </div>

      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>
          close
        </button>
      </form>
    </dialog>
  )
}

export default AdminSignupModal
